import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AclPermissionDetail } from '@y42/acl/models';
import { createAclPermissionDetail } from '@y42/acl/util/create-acl-permission-detail';
import { DataNoRowOverlayComponent } from '@y42/ag-grid';
import { InputObservable, connectState } from '@y42/angular/util';
import { animationAppearDisappear } from '@y42/animation';
import {
  CellClickedEvent,
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  RowClickedEvent,
  RowDoubleClickedEvent,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { Observable, combineLatest, debounceTime, delay, filter, map, of, switchMap, take, tap } from 'rxjs';
import { AbstractListViewService } from '../abstract-list-view.service';
import { ListViewDataBase, ListViewGridMessages } from '../list-view.model';

@UntilDestroy()
@Component({
  selector: 'y42-list-view-grid',
  templateUrl: './list-view-grid.component.html',
  styleUrls: ['./list-view-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [animationAppearDisappear()],
})
export class ListViewGridComponent<D extends ListViewDataBase = ListViewDataBase> implements OnInit {
  constructor(public readonly service: AbstractListViewService<D>) {}

  @Input() name = '';
  @Input() columnDefs: ColDef[] = [];
  @Input() gridOptions: GridOptions = {};
  @Input() frameworkComponents: GridOptions['components'] = {
    dataNoRowOverlayComponent: DataNoRowOverlayComponent,
  };
  @Input() messages?: ListViewGridMessages;

  @Input() searchCtrl!: FormControl<string>;
  @Input() hideLoader = false;
  @Input() hideNoDataImg = false;

  @Input()
  @InputObservable()
  incrementalLoading = false;
  private readonly incrementalLoading$!: Observable<boolean>;

  @Input()
  @InputObservable()
  addPermissionAllowed = createAclPermissionDetail({ allowed: false });
  private readonly addPermissionAllowed$!: Observable<AclPermissionDetail>;

  @Input() canAdd = true;

  @Output() add = new EventEmitter<void>();
  @Output() gridReady = new EventEmitter<GridReadyEvent>();
  @Output() gridRowClicked = new EventEmitter<RowClickedEvent>();
  @Output() gridRowDoubleClicked = new EventEmitter<RowDoubleClickedEvent>();
  @Output() gridSelectionChanged = new EventEmitter<SelectionChangedEvent>();
  @Output() cellClicked = new EventEmitter<CellClickedEvent>();

  private gridApi!: GridApi;
  readonly state = connectState(this, {
    data: this.service.items$,
    hasData: this.service.items$.pipe(map((items) => !!items?.length)),
    isLoading: this.service.loading$,
    initialized: combineLatest([this.service.loading$, this.incrementalLoading$]).pipe(
      switchMap(([loading, incrementalLoading]) => {
        if (!incrementalLoading) {
          return of(loading);
        }

        if (!loading) {
          return of(false);
        }
        return this.service.items$.pipe(map((items) => items.length === 0));
      }),
      filter((loading) => !loading),
      // give the quick-filters time to initialize
      // otherwise the whole page jumps a bit
      delay(200),
      map(() => true),
      take(1),
    ),
    addPermissionAllowed: this.addPermissionAllowed$,
  });

  ngOnInit() {
    this.searchCtrl.valueChanges
      .pipe(
        debounceTime(100),
        tap((value) => this.gridApi.setQuickFilter(value)),
        untilDestroyed(this),
      )
      .subscribe();
  }

  onGridReady(params: GridReadyEvent) {
    const gridApi = params.api;
    this.gridApi = gridApi;
    this.service.setGridApi(gridApi);
    this.gridReady.emit(params);
  }

  onGridSizeChanged() {
    this.gridApi.sizeColumnsToFit();
  }

  rowClicked(event: RowClickedEvent) {
    this.gridRowClicked.emit(event);
    this.service.edit?.(event);
  }

  rowDoubleClicked(event: RowClickedEvent) {
    this.gridRowDoubleClicked.emit(event);
  }
}
